import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FileSpreadsheet, ArrowLeft, Send, CheckCircle2 } from 'lucide-react';
import { upload, post, get, download } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useEvents } from '../context/EventContext';
import { useToast } from '../context/ToastContext';
import { Button, Modal, Badge, Select, Field, Input } from './UI';

/**
 * Roster import in three steps: pick the file → map its columns to fields →
 * see the result and send everyone their login details.
 *
 * The file goes up twice: once for the preview that proposes the mapping, then
 * once more with the mapping confirmed. That second upload only stages the
 * sheet; the rows are imported in slices afterwards, because a few hundred
 * people take minutes of database work and no browser holds a request open
 * that long. The progress bar is those slices landing.
 */
export default function ImportWizard({ open, onClose, onImported, onOpenSheet, eventId: forcedEvent }) {
  const { user, isSuperAdmin } = useAuth();
  const { eventId: contextEvent, events } = useEvents();
  const eventId = forcedEvent || contextEvent;
  const toast = useToast();
  const fileRef = useRef(null);

  const [step, setStep] = useState('file');          // file | map | done
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [extras, setExtras] = useState({});     // column index → label to keep it under ('' = skip)
  const [defaults, setDefaults] = useState({ onwardDate: '', returnDate: '' });
  const [corporate, setCorporate] = useState('');
  const [corporates, setCorporates] = useState([]);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);    // { done, total }
  const [sending, setSending] = useState(null);      // { done, total, failed }

  const event = events.find((e) => e._id === eventId);

  useEffect(() => {
    if (!open) return;
    setStep('file'); setFile(null); setPreview(null); setResult(null); setSending(null); setProgress(null); setCorporate('');
  }, [open]);

  // Company list for the mapping step (super admin / staff on multi-company events).
  useEffect(() => {
    if (!open || !isSuperAdmin) return;
    get('/corporates', { limit: 200 }).then((d) => setCorporates(Array.isArray(d) ? d : d?.rows || [])).catch(() => {});
  }, [open, isSuperAdmin]);

  const scopeFields = () => ({
    ...(eventId ? { eventId } : {}),
    ...(corporate ? { corporate } : (!isSuperAdmin && user?.corporate ? { corporate: user.corporate } : {})),
  });

  const runPreview = async () => {
    if (!file) return toast.error('Choose an .xlsx or .csv file first');
    setBusy(true);
    try {
      const res = await upload('/users/import/preview', file, scopeFields());
      const p = res.data;
      setPreview(p);
      setMapping(p.suggested || {});
      setExtras({});
      setDefaults({ onwardDate: p.hints?.onwardDate || '', returnDate: p.hints?.returnDate || '' });
      if (p.corporate) setCorporate(String(p.corporate));
      if (p.ready && (p.corporate || !p.needsCorporate)) {
        // Same layout as our template/export: no matching needed.
        await runImport(p.suggested || {}, { onwardDate: p.hints?.onwardDate || '', returnDate: p.hints?.returnDate || '' }, p.corporate ? String(p.corporate) : corporate);
      } else {
        setStep('map');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const runImport = async (map = mapping, dates = defaults, corp = corporate) => {
    if (map.name === undefined || map.name === '') return toast.error('Choose the column that holds the name');
    if (['mobile', 'employeeCode', 'email'].every((k) => map[k] === undefined || map[k] === '')) {
      return toast.error('Choose a column that identifies each person: mobile number, employee code or email');
    }
    if (preview?.needsCorporate && !corp) return toast.error('Choose which company these people belong to');
    setBusy(true);
    setProgress(null);
    try {
      const fields = {
        ...(eventId ? { eventId } : {}),
        ...(corp ? { corporate: corp } : (!isSuperAdmin && user?.corporate ? { corporate: user.corporate } : {})),
        mapping: JSON.stringify({ ...map, extras: Object.fromEntries(Object.entries(extras).filter(([, label]) => label && label.trim()).map(([i, label]) => [label.trim(), Number(i)])) }),
        defaults: JSON.stringify(dates),
      };

      // Stage the file: read and kept, nobody written yet. Sending the same
      // file again resumes that upload instead of starting a second one, so a
      // retry never doubles anybody.
      const staged = await upload('/users/import/stage', file, fields, 'file', { timeout: 180000 });
      const { sheetId, rowCount, chunk, processed = 0 } = staged.data;
      setProgress({ done: processed, total: rowCount });

      // Then the rows, a slice per request, until the server says it is done.
      let offset = processed;
      let last = null;
      for (let guard = 0; guard < 2000; guard += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await post('/users/import/run',
          { sheetId, offset, limit: chunk || 50 },
          { timeout: 120000 });
        last = res;
        const d = res.data || {};
        setProgress({ done: d.processed ?? offset, total: d.rowCount || rowCount });
        if (d.done) break;
        if (d.next === undefined || d.next <= offset) break;   // nothing moved: stop rather than spin
        offset = d.next;
      }

      const d = last?.data || {};
      setResult({
        ...d,
        created: d.totals?.created ?? d.created ?? 0,
        updated: d.totals?.updated ?? d.updated ?? 0,
        skipped: d.totals?.skipped ?? d.skipped ?? 0,
      });
      setStep('done');
      toast.success(last?.message || 'Import finished');
      onImported?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  /** Sends login details in batches of 25 so a big roster never times out. */
  const sendCredentials = async () => {
    const ids = result?.userIds || [];
    if (!ids.length) return;
    const state = { done: 0, total: ids.length, failed: [] };
    setSending({ ...state });
    for (let i = 0; i < ids.length; i += 25) {
      try {
        const res = await post('/users/credentials/send', { users: ids.slice(i, i + 25), event: eventId || undefined });
        state.done += res.data?.sent || 0;
        state.failed.push(...(res.data?.failed || []));
      } catch (err) {
        state.failed.push({ name: `${Math.min(25, ids.length - i)} people`, reason: err.message });
      }
      setSending({ ...state });
    }
    toast[state.failed.length ? 'error' : 'success'](
      state.failed.length ? `Sent to ${state.done}; ${state.failed.length} failed` : `Login details sent to ${state.done} people`,
    );
  };

  const downloadTemplate = async () => {
    try { await download('/users/import/template', {}, 'participants-template.xlsx'); }
    catch (err) { toast.error(err.message); }
  };

  const columnsFor = preview?.columns || [];
  const fields = preview?.fields || [];
  const groups = [
    ['person', 'About the person'],
    ['party', 'Who is coming with them'],
    ['manager', 'Who they report to'],
    ['onward', 'Onward flight'],
    ['return', 'Return flight'],
  ];
  const usedBy = (idx) => fields.filter((f) => String(mapping[f.key]) === String(idx)).map((f) => f.label);

  const footer = step === 'file' ? (
    <>
      <Button variant="subtle" onClick={onClose}>Close</Button>
      <Button onClick={runPreview} loading={busy} disabled={!file}>
        {progress ? `Importing ${progress.done} of ${progress.total}…` : 'Upload'}
      </Button>
    </>
  ) : step === 'map' ? (
    <>
      <Button variant="subtle" icon={ArrowLeft} onClick={() => setStep('file')} disabled={busy}>Back</Button>
      <Button onClick={() => runImport()} loading={busy}>
        {progress ? `Importing ${progress.done} of ${progress.total}…` : `Import ${preview?.rowCount} rows`}
      </Button>
    </>
  ) : (
    <>
      <Button variant="subtle" onClick={onClose}>Close</Button>
      <Button icon={Send} onClick={sendCredentials} loading={Boolean(sending) && sending.done + sending.failed.length < sending.total}
        disabled={!result?.userIds?.length || (sending && sending.done + sending.failed.length >= sending.total)}>
        {sending ? `Sent ${sending.done} of ${sending.total}` : `Send login details to ${result?.userIds?.length || 0} people`}
      </Button>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} width={step === 'map' ? 760 : 560}
      title={step === 'file' ? 'Import participant list' : step === 'map' ? 'Match the columns' : 'Import finished'}
      subtitle={step === 'file' ? 'Upload an .xlsx or .csv — our layout imports directly, any other lets you match the columns'
        : step === 'map' ? `${preview?.rowCount} rows found in "${preview?.sheetName}" (headers on row ${preview?.headerRow})`
        : event ? `People are now on ${event.title}` : undefined}
      footer={footer}
    >
      {progress && step !== 'done' && (
        <div style={{ marginBottom: 16, padding: 13, background: 'var(--ink-900)', border: '1px solid var(--border)', borderRadius: 'var(--r)' }}>
          <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginBottom: 8 }}>
            Importing {progress.done} of {progress.total} rows — the sheet is on the server now, so leave this open
            until it finishes.
          </p>
          <div style={{ height: 6, borderRadius: 99, background: 'var(--ink-800)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%`,
              background: 'var(--gold-400)', transition: 'width .3s',
            }} />
          </div>
        </div>
      )}

      {step === 'file' && (
        <>
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files?.[0] || null); }}
            style={{
              padding: 32, textAlign: 'center', cursor: 'pointer',
              border: `1.5px dashed ${file ? 'var(--gold-500)' : 'var(--border)'}`,
              borderRadius: 'var(--r-lg)', background: file ? 'rgba(239,212,138,0.05)' : 'var(--ink-900)',
            }}
          >
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <FileSpreadsheet size={30} color={file ? 'var(--gold-400)' : 'var(--text-lo)'} style={{ marginBottom: 11 }} />
            <p style={{ fontSize: 13.5, fontWeight: 500 }}>{file ? file.name : 'Drop your file here, or click to browse'}</p>
            <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 6 }}>
              {file ? `${(file.size / 1024).toFixed(0)} KB — ready` : 'Accepted: .xlsx, .xls, .csv — for example a traveller list from your agent'}
            </p>
          </div>

          <div style={{ marginTop: 18, padding: 14, background: 'var(--ink-800)', border: '1px solid var(--border)', borderRadius: 'var(--r)' }}>
            <p style={{ fontSize: 12.5, color: 'var(--text-mid)', lineHeight: 1.7 }}>
              A sheet in our own layout (the template below, or an Excel export of this list) imports straight away.
              Any other layout — an agent's traveller list, an HR export — shows a matching step where you say which column is which.
            </p>
            <p style={{ fontSize: 12.5, color: 'var(--text-lo)', lineHeight: 1.7, marginTop: 8 }}>
              People are recognised by <strong>mobile number</strong> or <strong>employee code</strong>, so re-uploading the same sheet updates
              them rather than adding duplicates.{event?.modules?.flights !== false && event ? ' Flight columns (flight no, PNR, from, to, times) go straight into their travel.' : ''}
            </p>
            <Button variant="outline" size="sm" icon={FileSpreadsheet} onClick={downloadTemplate} style={{ marginTop: 12 }}>
              Download the template
            </Button>
          </div>
        </>
      )}

      {step === 'map' && preview && (
        <>
          {(preview.needsCorporate || (isSuperAdmin && corporates.length > 0)) && (
            <Field label="Company these people belong to" required>
              <Select value={corporate} onChange={(e) => setCorporate(e.target.value)}>
                <option value="">Choose a company…</option>
                {(preview.corporates?.length ? preview.corporates : corporates)
                  .map((c) => <option key={c._id} value={c._id}>{c.name || c._id}</option>)}
              </Select>
            </Field>
          )}

          {groups.map(([g, title]) => {
            const list = fields.filter((f) => f.group === g);
            if (!list.length) return null;
            return (
              <div key={g} style={{ marginTop: 16 }}>
                <p style={{ fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-lo)', marginBottom: 8 }}>{title}</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 8 }}>
                  {list.map((f) => {
                    const idx = mapping[f.key];
                    const col = idx !== undefined && idx !== '' ? columnsFor[idx] : null;
                    const must = f.required;
                    return (
                      <div key={f.key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, alignItems: 'center', padding: '8px 10px', borderRadius: 'var(--r)', border: `1px solid ${must && !col ? 'var(--warn)' : 'var(--border)'}`, background: 'var(--ink-900)' }}>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 500 }}>{f.label}{must ? <span style={{ color: 'var(--gold-400)' }}> *</span> : null}</p>
                          <p style={{ fontSize: 11, color: 'var(--text-lo)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {col?.samples?.length ? `e.g. ${col.samples.slice(0, 2).join(', ')}` : col ? 'no sample values' : 'not imported'}
                          </p>
                        </div>
                        <Select value={idx === undefined ? '' : String(idx)} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value === '' ? undefined : Number(e.target.value) })} style={{ fontSize: 12.5 }}>
                          <option value="">— skip —</option>
                          {columnsFor.map((c) => (
                            <option key={c.index} value={c.index}>{c.name}{usedBy(c.index).filter((l) => l !== f.label).length ? ' (also used)' : ''}</option>
                          ))}
                        </Select>
                      </div>
                    );
                  })}
                </div>
                {g === 'party' && (
                  <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 8, lineHeight: 1.7 }}>
                    Either column is enough — a total that includes them is turned into the number coming with them.
                    This is what the check-in form compares against: arriving with more people than the sheet said is
                    flagged on the Users list, the same or fewer is cleared.
                  </p>
                )}
                {g === 'onward' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginTop: 8 }}>
                    <Field label="Onward date when the sheet has no date column" hint={preview.hints?.onwardDate ? 'Read from the sheet heading' : undefined}>
                      <Input type="date" value={defaults.onwardDate} onChange={(e) => setDefaults({ ...defaults, onwardDate: e.target.value })} />
                    </Field>
                    <Field label="Return date when the sheet has no date column" hint={preview.hints?.returnDate ? 'Read from the sheet heading' : undefined}>
                      <Input type="date" value={defaults.returnDate} onChange={(e) => setDefaults({ ...defaults, returnDate: e.target.value })} />
                    </Field>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {step === 'done' && result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', padding: 15, background: 'var(--ink-900)', border: '1px solid var(--border)', borderRadius: 'var(--r)' }}>
            <Result label="Added" value={result.created} tone="var(--ok)" />
            <Result label="Updated" value={result.updated} tone="var(--info)" />
            <Result label="Skipped" value={result.skipped} tone="var(--warn)" />
            {result.flights > 0 && <Result label="Flight legs" value={result.flights} tone="var(--gold-300)" />}
          </div>

          {result.errors?.length > 0 && (
            <div style={{ maxHeight: 150, overflowY: 'auto', marginTop: 12, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r)' }}>
              {result.errors.slice(0, 40).map((e, i) => (
                <p key={i} style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 4 }}>
                  <span style={{ color: 'var(--warn)' }}>Row {e.line}</span> — {e.reason}
                </p>
              ))}
            </div>
          )}

          {result.sheetId && (
            <div style={{ marginTop: 14, padding: 13, background: 'var(--ink-900)', border: '1px solid var(--border)', borderRadius: 'var(--r)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <p style={{ fontSize: 13, fontWeight: 600 }}>The sheet has been kept in full</p>
                <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 4, lineHeight: 1.6 }}>
                  Every column of the file is stored as it arrived — including the ones nothing was matched to — so you can
                  open the whole table later.
                </p>
              </div>
              {onOpenSheet && (
                <Button variant="outline" size="sm" icon={FileSpreadsheet}
                  onClick={() => { onOpenSheet(result.sheetId); onClose?.(); }}>
                  Open full sheet
                </Button>
              )}
            </div>
          )}

          <div style={{ marginTop: 16, padding: 14, background: 'var(--ink-800)', border: '1px solid var(--border)', borderRadius: 'var(--r)' }}>
            <p style={{ fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><Send size={14} color="var(--gold-500)" /> Send everyone their login details</p>
            <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 5, lineHeight: 1.6 }}>
              Each person gets a WhatsApp message and an email (whichever they have) with the web link,
              their login id (email, or mobile number when there is no email) and a password. People who already
              received a password get the same one again.
            </p>
            {sending && (
              <div style={{ marginTop: 10 }}>
                <div style={{ height: 6, borderRadius: 99, background: 'var(--ink-900)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round(((sending.done + sending.failed.length) / sending.total) * 100)}%`, background: 'var(--gold-400)', transition: 'width .3s' }} />
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {sending.done + sending.failed.length >= sending.total ? <CheckCircle2 size={13} color="var(--ok)" /> : null}
                  {sending.done} sent{sending.failed.length ? `, ${sending.failed.length} failed` : ''}
                </p>
                {sending.failed.slice(0, 10).map((f, i) => (
                  <p key={i} style={{ fontSize: 12, color: 'var(--text-mid)' }}><span style={{ color: 'var(--warn)' }}>{f.name}</span> — {f.reason}</p>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
      {step === 'map' && preview && (() => {
        const used = new Set(Object.values(mapping).filter((v) => v !== undefined && v !== ''));
        const spare = columnsFor.filter((c) => !used.has(c.index) && c.samples?.length);
        if (!spare.length) return null;
        return (
          <div style={{ marginTop: 18 }}>
            <p style={{ fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-lo)', marginBottom: 4 }}>Other columns in the sheet</p>
            <p style={{ fontSize: 12, color: 'var(--text-lo)', marginBottom: 8, lineHeight: 1.6 }}>
              Not matched to anything above. Tick a column to keep it on each person as an extra field (shown in their record and in exports); give it any name you like.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 8 }}>
              {spare.map((c) => {
                const on = extras[c.index] !== undefined;
                return (
                  <div key={c.index} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 8, alignItems: 'center', padding: '8px 10px', borderRadius: 'var(--r)', border: '1px solid var(--border)', background: on ? 'rgba(239,212,138,0.05)' : 'var(--ink-900)' }}>
                    <input type="checkbox" checked={on} style={{ accentColor: 'var(--gold-400)' }}
                      onChange={(e) => { const n = { ...extras }; if (e.target.checked) n[c.index] = c.name; else delete n[c.index]; setExtras(n); }} />
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</p>
                      <p style={{ fontSize: 11, color: 'var(--text-lo)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>e.g. {c.samples.slice(0, 2).join(', ')}</p>
                    </div>
                    <Input value={on ? extras[c.index] : ''} disabled={!on} placeholder="Field name" style={{ fontSize: 12.5 }}
                      onChange={(e) => setExtras({ ...extras, [c.index]: e.target.value })} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {step === 'map' && (
        <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 14, lineHeight: 1.6 }}>
          <Badge tone="gold">*</Badge> required. Titles like "Mr." and "Ms." are removed from names and used to fill in gender when
          the sheet has no gender column.
        </p>
      )}
    </Modal>
  );
}

function Result({ label: text, value, tone }) {
  return (
    <div>
      <p style={{ fontSize: 22, fontWeight: 700, color: tone, fontVariantNumeric: 'tabular-nums' }}>{value}</p>
      <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 2 }}>{text}</p>
    </div>
  );
}
